import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { Agent, ListingSummary, SearchOptions } from '@/types'

import Link from 'next/link'
import { useState } from 'react'
import { MessageCircle, Phone } from 'lucide-react'
import { getAgents, getCatalog, getSearchOptions } from '@/lib/catalog'
import { formatCompactPrice, plural } from '@/lib/format'
import { emptySearchOptions } from '@/lib/search'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Photo } from '@/components/generals/photo'
import { AgentAvatar } from '@/components/agents/agent-avatar'
import { ContactForm } from '@/components/agents/contact-form'

interface AgentsPageProps {
    agents: Agent[]
    listings: ListingSummary[]
    searchOptions: SearchOptions
}

const AgentsPage: FC<Partial<AgentsPageProps>> = ({ agents = [], listings = [], searchOptions = emptySearchOptions }): ReactNode => {
    const [agentId, setAgentId] = useState('')

    const contact = (id: string) => {
        setAgentId(id)
        document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    return (
        <Layout searchOptions={searchOptions}>
            <Seo title='Our agents' description='Meet the Viewfinder Realty agents who sell homes across coastal, desert, wine country and mountain California.' />

            <div className='mx-auto max-w-[1760px] px-4 sm:px-6 lg:px-10'>
                <section id='team' aria-labelledby='team-title' className='py-12'>
                    <h1 id='team-title' className='font-heading text-4xl font-extrabold tracking-tight sm:text-5xl'>Meet your agents</h1>
                    <p className='mt-3 max-w-2xl text-lg text-muted-foreground'>
                        Each of us lives and works in the places we sell, so you get straight answers about streets, schools, weather and value.
                    </p>

                    {agents.length === 0 && <p className='mt-10 text-muted-foreground'>Our team is being updated. Please check back in a moment.</p>}

                    <ul className='mt-10 grid gap-6 lg:grid-cols-2'>
                        {agents.map(agent => {
                            const agentListings = listings.filter(listing => listing.agent_id === agent.id)

                            return (
                                <li key={agent.id} id={`agent-${agent.slug}`} className='flex flex-col gap-6 rounded-3xl border border-border p-6 sm:flex-row'>
                                    <AgentAvatar agent={agent} className='size-28 sm:size-36' sizes='144px' />

                                    <div className='min-w-0 flex-1'>
                                        <h2 className='text-xl font-semibold'>
                                            <span data-mdlfy-cms={`agents:${agent.id}:name`}>{agent.name}</span>
                                        </h2>
                                        {agent.role && (
                                            <p className='text-sm text-muted-foreground'>
                                                <span data-mdlfy-cms={`agents:${agent.id}:role`}>{agent.role}</span>
                                            </p>
                                        )}
                                        {agent.bio && (
                                            <p className='mt-3 text-sm leading-relaxed'>
                                                <span data-mdlfy-cms={`agents:${agent.id}:bio`}>{agent.bio}</span>
                                            </p>
                                        )}

                                        {agentListings.length > 0 && (
                                            <div className='mt-5'>
                                                <p className='text-sm font-medium'>
                                                    <span>{agentListings.length}</span> active {plural(agentListings.length, 'listing')}
                                                </p>
                                                <ul className='mt-2 flex gap-2'>
                                                    {agentListings.slice(0, 4).map(listing => (
                                                        <li key={listing.id}>
                                                            <Link href={`/homes/${listing.slug}`} className='group block w-20'>
                                                                <span className='relative block aspect-square overflow-hidden rounded-lg bg-muted'>
                                                                    <Photo src={listing.cover} alt={listing.cover_alt} sizes='80px' className='transition group-hover:scale-105' />
                                                                </span>
                                                                <span className='mt-1 block text-xs font-semibold'>{formatCompactPrice(listing.price)}</span>
                                                                <span className='sr-only' data-mdlfy-cms={`listings:${listing.id}:title`}>{listing.title}</span>
                                                            </Link>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        <div className='mt-5 flex flex-wrap gap-3'>
                                            {agent.phone && (
                                                <a
                                                    href={`tel:${agent.phone.replace(/[^0-9+]/g, '')}`}
                                                    className='flex h-11 items-center gap-2 rounded-xl border border-foreground px-4 text-sm font-semibold transition hover:bg-secondary'
                                                >
                                                    <Phone className='size-4' aria-hidden='true' />
                                                    <span data-mdlfy-cms={`agents:${agent.id}:phone`}>{agent.phone}</span>
                                                </a>
                                            )}
                                            <button
                                                type='button'
                                                onClick={() => contact(agent.id)}
                                                className='flex h-11 items-center gap-2 rounded-xl bg-foreground px-4 text-sm font-semibold text-background'
                                            >
                                                <MessageCircle className='size-4' aria-hidden='true' />
                                                Send a message
                                            </button>
                                        </div>
                                    </div>
                                </li>
                            )
                        })}
                    </ul>
                </section>

                <section id='contact' aria-labelledby='contact-title' className='scroll-mt-24 pb-16'>
                    <div className='grid gap-10 rounded-3xl bg-secondary p-6 sm:p-10 lg:grid-cols-[1fr_1.4fr]'>
                        <div>
                            <h2 id='contact-title' className='font-heading text-3xl font-bold tracking-tight'>Talk to our team</h2>
                            <p className='mt-3 text-muted-foreground'>
                                Questions about a home, an area or the market? Send a note and the right person will get back to you within one business day.
                            </p>
                        </div>

                        <div className='rounded-2xl bg-white p-6 sm:p-8'>
                            <ContactForm agents={agents} agentId={agentId} onAgentChange={setAgentId} />
                        </div>
                    </div>
                </section>
            </div>
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<AgentsPageProps> = async () => {
    try {
        const [catalog, agents] = await Promise.all([getCatalog(), getAgents()])

        return {
            props: { agents, listings: catalog.listings, searchOptions: getSearchOptions(catalog) },
            revalidate: 60
        }
    }
    catch (error) {
        console.error('Agents page data failed to load:', error instanceof Error ? error.message : error)

        return {
            props: { agents: [], listings: [], searchOptions: emptySearchOptions },
            revalidate: 10
        }
    }
}

export default AgentsPage
